import { UrlObject } from 'url'
import { FormTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { BlockTypeEnum } from '@omnea-app/packages/universal/workflow/blockType'
import { RequestTopLevelFilterKey } from '@omnea-app/requests/requestsFrontendClient'
import type { GridRowId } from '@mui/x-data-grid-premium'
import type { RequestDetailPageTab, RequestRiskSection } from '@omnea-app/fe-routing/routes/detailTabs'
import type { AgentAssessmentBlockType } from '@omnea-app/packages/universal/workflow/isAgentAssessmentBlockType'

export const generateRequestsListRoute = (mode?: RequestTopLevelFilterKey) => {
  if (!mode) {
    // The requests page will determine the tab based on local storage or user's role
    return '/requests'
  }
  switch (mode) {
    case 'mine':
      return '/requests/mine'
    case 'all':
      return '/requests/all'
    case 'pending':
      return '/requests/pending'
    case 'minePending':
      return '/requests/my-pending'
    case 'delayed':
      return '/requests/delayed'
    case 'mineDelayed':
      return '/requests/my-delayed'
    case 'completed':
      return '/requests/completed'
    case 'mineCompleted':
      return '/requests/my-completed'
    case 'cancelled':
      return '/requests/cancelled'
    case 'mineCancelled':
      return '/requests/my-cancelled'
    case 'paused':
      return '/requests/paused'
    case 'minePaused':
      return '/requests/my-paused'
    case 'rejected':
      return '/requests/rejected'
    case 'following':
      return '/requests/following'
    case 'owned':
      return '/requests/owned'
    case 'waiting-for-you':
      return '/requests/waiting-for-you'
    case 'blocked':
      return '/requests/blocked'
    case 'awaiting-receipt':
      return '/requests/awaiting-receipt'
    case 'rejected-tasks':
      return '/requests/rejected-tasks'
    case 'errored-tasks':
      return '/requests/errored-tasks'
    default: {
      assertNever(mode)
      return `/requests/${mode}`
    }
  }
}

export const generateRequestDetailRoute = (tab: RequestTabs, requestId: string | GridRowId, taskId?: string, formId?: string) => {
  switch (tab) {
    case RequestTabs.OVERVIEW:
      return `/requests/detail/${requestId}/overview`
    case RequestTabs.INTAKE_FORM:
      return `/requests/detail/${requestId}/intake-form`
    case RequestTabs.SOURCE:
      return `/requests/detail/${requestId}/source`
    case RequestTabs.SUBMISSIONS:
      return `/requests/detail/${requestId}/submissions${formId ? `/${formId}` : ''}`
    case RequestTabs.RENEWAL_ASSESSMENT_FORM:
      return `/requests/detail/${requestId}/renewal-assessment${formId ? `/${formId}` : ''}`
    case RequestTabs.MY_TASKS:
      return `/requests/detail/${requestId}/my-tasks${taskId ? `/${taskId}` : ''}`
    case RequestTabs.DOCUMENTS:
      return `/requests/detail/${requestId}/documents`
    case RequestTabs.RISKS:
      return `/requests/detail/${requestId}/risks`
    case RequestTabs.ARTEFACTS:
      return `/requests/detail/${requestId}/artefacts`
    case RequestTabs.RFX:
      return `/requests/detail/${requestId}/rfx/overview`
    case RequestTabs.AUCTIONS:
      return `/requests/detail/${requestId}/auctions`
    case RequestTabs.WORKFLOW_SNAPSHOT:
      return `/requests/detail/${requestId}/workflow`
    case RequestTabs.LINE_ITEMS:
      return `/requests/detail/${requestId}/line-items`
    default: {
      assertNever(tab)
      throw new Error(`Unhandled tab: ${tab}`)
    }
  }
}

export const generateRequestRiskSectionRoute = (requestId: string, section: RequestRiskSection) =>
  `${generateRequestDetailRoute(RequestTabs.RISKS, requestId)}/${section}`

export const AGENT_ASSESSMENT_RISK_SECTION: Record<AgentAssessmentBlockType, RequestRiskSection> = {
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: 'inherent-risk',
  [BlockTypeEnum.RISK_CONTROLS_AGENT]: 'controls',
}

type AgentAssessmentFormType = FormTypeEnum.INHERENT_RISK_ASSESSMENT | FormTypeEnum.RISK_CONTROLS_ASSESSMENT

// These forms show only in their risk section, which has no anchor for a single question.
const AGENT_ASSESSMENT_FORM_RISK_SECTION: Record<AgentAssessmentFormType, RequestRiskSection> = {
  [FormTypeEnum.INHERENT_RISK_ASSESSMENT]: 'inherent-risk',
  [FormTypeEnum.RISK_CONTROLS_ASSESSMENT]: 'controls',
}

const isAgentAssessmentFormType = (formType: string): formType is AgentAssessmentFormType =>
  Object.hasOwn(AGENT_ASSESSMENT_FORM_RISK_SECTION, formType)

const formTypeEnumValues = new Set<string>(Object.values(FormTypeEnum))
const isFormTypeEnum = (value: string): value is FormTypeEnum => formTypeEnumValues.has(value)

export const getTabForFormType = (formType: string | null | undefined): RequestTabs => {
  if (!formType || !isFormTypeEnum(formType)) return RequestTabs.SUBMISSIONS

  switch (formType) {
    case FormTypeEnum.INTAKE:
    case FormTypeEnum.ACCESS_REQUEST:
    case FormTypeEnum.CATALOGUE_PURCHASE:
      return RequestTabs.INTAKE_FORM
    case FormTypeEnum.RENEWAL_ASSESSMENT:
      return RequestTabs.RENEWAL_ASSESSMENT_FORM
    case FormTypeEnum.CANCEL_AGREEMENT:
    case FormTypeEnum.CANCEL_ASSESSMENTS:
    case FormTypeEnum.COMMIT_SUPPLIER_DATA:
    case FormTypeEnum.CREATE_AGREEMENT:
    case FormTypeEnum.CREATE_ASSESSMENT:
    case FormTypeEnum.CREATE_ENGAGEMENT:
    case FormTypeEnum.CREATE_PURCHASE_ORDER:
    case FormTypeEnum.CREATE_RENEWAL:
    case FormTypeEnum.EXTERNAL_OPERATION:
    case FormTypeEnum.OTHER:
    case FormTypeEnum.RFX:
    case FormTypeEnum.SUPPLIER_ONBOARDING:
    case FormTypeEnum.CONTENT_GENERATION:
      return RequestTabs.SUBMISSIONS
    case FormTypeEnum.INHERENT_RISK_ASSESSMENT:
    case FormTypeEnum.RISK_CONTROLS_ASSESSMENT:
      return RequestTabs.RISKS
    default:
      assertNever(formType)
      return RequestTabs.SUBMISSIONS
  }
}

/**
 * Route to a question on a request's form: the tab that owns the form's type, the form itself, and the
 * `#<questionName>` anchor the form viewer scrolls to on mount. The anchor is encoded because the reader
 * decodes it, so a name containing `#`, `/` or a space still matches the question's element id.
 */
export const generateRequestQuestionRoute = ({
  requestId,
  requestFormId,
  formType,
  questionName,
}: {
  requestId: string
  requestFormId: string
  formType?: string | null
  questionName?: string | null
}) => {
  if (formType && isAgentAssessmentFormType(formType)) {
    return generateRequestRiskSectionRoute(requestId, AGENT_ASSESSMENT_FORM_RISK_SECTION[formType])
  }
  const formRoute = generateRequestDetailRoute(getTabForFormType(formType), requestId, undefined, requestFormId)
  return questionName ? `${formRoute}#${encodeURIComponent(questionName)}` : formRoute
}

export const rfxSections = [
  'activity',
  'settings',
  'suppliers',
  'questionnaires',
  'evaluation',
  'qna',
  'responses',
  'results',
  'related',
] as const
export type RfxSectionType = (typeof rfxSections)[number]

export const isValidRfxSection = (section: string | string[] | undefined): section is RfxSectionType =>
  typeof section === 'string' && rfxSections.includes(section as RfxSectionType)

export const isRfxDetailRoute = (route: string): boolean => route.startsWith('/requests/detail/[id]/rfx')

const generateRfxDetailRoute = (path: string, requestId: string, round: number | undefined): UrlObject => ({
  pathname: `/requests/detail/${requestId}/rfx/${path}`,
  query: round !== undefined ? { round } : {},
})

export const generateRfxResultsBreakdownRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => generateRfxDetailRoute(`results/results-breakdown/${requestFormTemplateId}`, requestId, round)

export const generateRfxResponsesFormRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => generateRfxDetailRoute(`responses/${requestFormTemplateId}`, requestId, round)

export const generateRfxResponsesSupplierFormsRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => generateRfxDetailRoute(`responses/${requestFormTemplateId}/supplier-forms`, requestId, round)

export const generateRfxResponsesScoringRoute = (requestId: string, round: number | undefined, taskId: string): UrlObject =>
  generateRfxDetailRoute(`responses/scoring/${taskId}`, requestId, round)

export const generateRfxPricingAnalysisRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => generateRfxDetailRoute(`responses/${requestFormTemplateId}/pricing-analysis`, requestId, round)

export const parseRequestDetailRoute = (tab: RequestDetailPageTab) => {
  switch (tab) {
    case 'overview':
      return RequestTabs.OVERVIEW
    case 'intake-form':
      return RequestTabs.INTAKE_FORM
    case 'source':
      return RequestTabs.SOURCE
    case 'submissions':
      return RequestTabs.SUBMISSIONS
    case 'renewal-assessment':
      return RequestTabs.RENEWAL_ASSESSMENT_FORM
    case 'my-tasks':
      return RequestTabs.MY_TASKS
    case 'documents':
      return RequestTabs.DOCUMENTS
    case 'risks':
      return RequestTabs.RISKS
    case 'artefacts':
      return RequestTabs.ARTEFACTS
    case 'auctions':
      return RequestTabs.AUCTIONS
    case 'workflow':
      return RequestTabs.WORKFLOW_SNAPSHOT
    case 'line-items':
      return RequestTabs.LINE_ITEMS
    default: {
      assertNever(tab)
      return RequestTabs.OVERVIEW
    }
  }
}

export enum RequestTabs {
  OVERVIEW = 'OVERVIEW',
  INTAKE_FORM = 'INTAKE_FORM',
  SOURCE = 'SOURCE',
  RENEWAL_ASSESSMENT_FORM = 'RENEWAL_ASSESSMENT_FORM',
  SUBMISSIONS = 'SUBMISSIONS',
  MY_TASKS = 'MY_TASKS',
  DOCUMENTS = 'DOCUMENTS',
  RISKS = 'RISKS',
  ARTEFACTS = 'ARTEFACTS',
  RFX = 'RFX',
  AUCTIONS = 'AUCTIONS',
  WORKFLOW_SNAPSHOT = 'WORKFLOW_SNAPSHOT',
  LINE_ITEMS = 'LINE_ITEMS',
}
